/* Crew — the agents, read as real orchestrator state.
   Atlas coordinator (reads /api/atlas) · You→Atlas→leads→workers hierarchy ·
   agent cards (model / status / current-task / cost / tasks-done / tokens) that
   open a conversation THREAD dialog with per-agent actions · live trace ·
   per-agent cost · pipeline kanban · compass badge shelf. Every data region
   ships all four states via <Widget>/<WidgetBody>; unconnected sources keep
   their "connect me" badge. Actions are honest: message/assign POST the task
   bridge; pause/resume + spin-up are orchestrator [I DO]s (flagged, not faked).
   Architecture: docs/ATLAS.md + docs/DISPATCH_BRIDGE.md. */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dialog, StatusPill, Tag } from '../../ds';
import { WidgetBody } from '../../ds/mc-kit';
import { useContract, type WidgetState } from '../../lib/api';
import { integrationFor } from '../../lib/flags';
import { useOps } from '../ops';
import { deriveCrew, deriveTrace, deriveKanban, deriveBadges } from '../../lib/selectors';
import type { CrewMember, CrewThreadMsg } from '../../lib/contract';
import { Panel, Widget } from '../ui';
import { HeartbeatTile } from '../Heartbeat';

/* ---------------------------------------------------------------- helpers */

const mono: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' };
const subLabel: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' };

/** Model → Tag tone. Opus (rare/expensive) = iris, Sonnet = cyan, Haiku = neutral. */
function modelTone(m?: string): 'neutral' | 'iris' | 'cyan' {
  if (!m) return 'neutral';
  if (/opus/i.test(m)) return 'iris';
  if (/sonnet/i.test(m)) return 'cyan';
  return 'neutral';
}

async function postJson(url: string, body: unknown): Promise<Response> {
  const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res;
}

/** Direction cue for a thread message — word + shape, never color-only. */
const DIR: Record<CrewThreadMsg['dir'], { word: string; sub: string; arrow: string; align: CSSProperties['alignSelf']; soft: string; line: string; ink: string }> = {
  up: { word: 'up', sub: 'to lead', arrow: '↑', align: 'flex-end', soft: 'var(--primary-soft)', line: 'var(--primary-line)', ink: 'var(--primary)' },
  down: { word: 'down', sub: 'to crew', arrow: '↓', align: 'flex-start', soft: 'var(--accent-soft)', line: 'var(--accent-line)', ink: 'var(--accent-ink)' },
  self: { word: 'self', sub: 'own step', arrow: '•', align: 'stretch', soft: 'var(--surface2)', line: 'var(--border)', ink: 'var(--muted)' },
};

/* ---------------------------------------------------------------- Atlas hook */

interface AtlasData {
  inFlight: number;
  routedToCrew: { text: string; assignee: string }[];
  unrouted: { text: string }[];
  awaitingFounder: { kind: string; text: string; from: string }[];
  untriagedIdeas: number;
  router: { default: string; escalate: string; rare: string };
  heartbeat: string;
}

interface AtlasState {
  state: WidgetState;
  data: AtlasData | null;
  source: 'live' | 'placeholder' | null;
}

/** /api/atlas is not a contract key — fetch it directly, poll for the pulse,
    degrade to an honest error/connect-me note if it can't be reached. */
function useAtlas() {
  const [s, setS] = useState<AtlasState>({ state: 'loading', data: null, source: null });
  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/atlas', { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const json = (await res.json()) as { source: 'live' | 'placeholder'; data: AtlasData };
      if (!json || typeof json !== 'object' || !('data' in json)) throw new Error('malformed');
      setS({ state: 'live', data: json.data, source: json.source });
    } catch {
      setS((prev) => (prev.data ? prev : { state: 'error', data: null, source: null }));
    }
  }, []);
  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => clearInterval(id);
  }, [load]);
  return { ...s, refetch: () => void load() };
}

/* ---------------------------------------------------------------- Atlas panel */

function AtlasBody({ data, nav }: { data: AtlasData; nav: ReturnType<typeof useNavigate> }) {
  const routeFor = (kind: string) => (kind === 'ido' ? '/app/plan' : '/app/controls');
  const linkBtn: CSSProperties = {
    background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', cursor: 'pointer',
    color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontSize: 11, textDecoration: 'underline', textAlign: 'left',
  };
  return (
    <div className="mc-auto mc-auto-lg">
      {/* coordinating */}
      <div style={{ minWidth: 0 }}>
        <div style={subLabel}>coordinating</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '6px 0 10px' }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 26, color: 'var(--ink)' }}>{data.inFlight}</span>
          <span style={mono}>in flight</span>
        </div>
        {data.routedToCrew.length === 0 && data.unrouted.length === 0 ? (
          <div style={mono}>nothing routed right now</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {data.routedToCrew.map((t, i) => (
              <div key={`r${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink)', minWidth: 0 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.text}</span>
                <Tag tone="cyan" style={{ padding: '1px 8px', fontSize: 10, flex: 'none' }}>{t.assignee}</Tag>
              </div>
            ))}
            {data.unrouted.map((t, i) => (
              <div key={`u${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink)', minWidth: 0 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.text}</span>
                <Tag style={{ padding: '1px 8px', fontSize: 10, flex: 'none' }}>unrouted</Tag>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* escalated to you */}
      <div style={{ minWidth: 0 }}>
        <div style={subLabel}>escalated to you</div>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.awaitingFounder.length === 0 ? (
            <div style={mono}>nothing waiting on you</div>
          ) : (
            data.awaitingFounder.map((a, i) => (
              <div key={i} style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <Tag tone={a.kind === 'ido' ? 'iris' : 'cyan'} style={{ padding: '1px 8px', fontSize: 9.5 }}>{a.kind}</Tag>
                  <span style={{ ...mono }}>from {a.from || 'auto'}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink)', margin: '3px 0 2px', lineHeight: 1.45 }}>{a.text}</div>
                <button type="button" style={linkBtn} onClick={() => nav(routeFor(a.kind))}>
                  {a.kind === 'ido' ? 'Open in Plan →' : 'Review in Approvals →'}
                </button>
              </div>
            ))
          )}
          {data.untriagedIdeas > 0 && (
            <button type="button" style={linkBtn} onClick={() => nav('/app/plan')}>
              {data.untriagedIdeas} untriaged idea{data.untriagedIdeas > 1 ? 's' : ''} → triage in Plan
            </button>
          )}
        </div>
      </div>

      {/* model router */}
      <div style={{ minWidth: 0 }}>
        <div style={subLabel}>model router</div>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {[
            { label: 'default', model: data.router.default, tone: 'neutral' as const },
            { label: 'escalate', model: data.router.escalate, tone: 'cyan' as const },
            { label: 'rare', model: data.router.rare, tone: 'iris' as const },
          ].map((r) => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ ...subLabel, width: 62, flex: 'none' }}>{r.label}</span>
              <Tag tone={r.tone} style={{ padding: '2px 9px', fontSize: 10.5 }}>{r.model}</Tag>
            </div>
          ))}
          <div style={{ ...mono, lineHeight: 1.5 }}>routed by task class — see docs/ATLAS.md</div>
        </div>
      </div>
    </div>
  );
}

function AtlasPanel() {
  const nav = useNavigate();
  const { state, data, source, refetch } = useAtlas();
  const connect = integrationFor('crew')?.connect;
  const degraded = source === 'placeholder' || state === 'error';
  return (
    <Panel
      accent
      label="Atlas orchestrator"
      title="// atlas — orchestrator (scaffold)"
      aside={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {degraded && <Badge tone="warn">connect me</Badge>}
          {data && (
            <span style={{ ...mono, color: 'var(--accent-ink)' }}>
              {data.inFlight} in flight · {data.heartbeat}
            </span>
          )}
        </div>
      }
    >
      <WidgetBody
        state={state}
        rows={3}
        minHeight={150}
        errorLabel="Atlas unreachable"
        errorSub="The coordinator is a scaffold — nothing live to show yet."
        onRetry={refetch}
      >
        {data && <AtlasBody data={data} nav={nav} />}
      </WidgetBody>
      {degraded && connect && (
        <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
          {connect} · coordinator scaffold · showing sample state
        </div>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------- Hierarchy */

/** A clickable agent chip (glyph + name + status) → opens the thread. */
function AgentChip({ a, onOpen }: { a: ReturnType<typeof deriveCrew>[number]; onOpen: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(a.id)}
      aria-label={`Open ${a.name} conversation thread — ${a.status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '5px 11px 5px 8px',
        background: 'var(--surface)', border: `1px solid ${a.badgeLine}`, borderRadius: 'var(--radius-pill)',
        cursor: 'pointer', font: 'inherit', minWidth: 0,
      }}
    >
      <span style={{ fontSize: 16, flex: 'none' }}>{a.glyph}</span>
      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 12.5, color: 'var(--ink)' }}>{a.name}</span>
      <StatusPill status={a.status} />
    </button>
  );
}

const ARROW = (
  <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', color: 'var(--border)', lineHeight: 0.6, fontSize: 16 }}>↓</div>
);

function HierarchyTier({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
      <span style={{ ...subLabel, width: 96, flex: 'none' }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function Hierarchy({ data, onOpen }: { data: CrewMember[]; onOpen: (id: string) => void }) {
  const enriched = deriveCrew({ crew: data });
  const atlas = enriched.find((a) => a.reportsTo === '' || a.id === 'atlas');
  const leads = enriched.filter((a) => a.lead && a.id !== atlas?.id);
  const workers = enriched.filter((a) => !a.lead);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <HierarchyTier label="you">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '5px 12px', background: 'var(--primary-soft)', border: '1px solid var(--primary-line)', borderRadius: 'var(--radius-pill)' }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 12.5, color: 'var(--ink)' }}>Cassidy</span>
          <span style={{ ...mono, color: 'var(--primary)' }}>founder</span>
        </span>
      </HierarchyTier>
      {atlas && (
        <>
          {ARROW}
          <HierarchyTier label="orchestrator">
            <AgentChip a={atlas} onOpen={onOpen} />
          </HierarchyTier>
        </>
      )}
      {leads.length > 0 && (
        <>
          {ARROW}
          <HierarchyTier label="leads">
            {leads.map((a) => <AgentChip key={a.id} a={a} onOpen={onOpen} />)}
          </HierarchyTier>
        </>
      )}
      {workers.length > 0 && (
        <>
          {ARROW}
          <HierarchyTier label="crew">
            {workers.map((a) => <AgentChip key={a.id} a={a} onOpen={onOpen} />)}
          </HierarchyTier>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Thread dialog */

function ThreadDialog({
  agent, msgs, threadsPlaceholder, onClose,
}: {
  agent: CrewMember;
  msgs: CrewThreadMsg[];
  threadsPlaceholder: boolean;
  onClose: () => void;
}) {
  const { pushToast, appendAudit } = useOps();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const threadConnect = integrationFor('threads')?.connect;

  const dispatch = async (kind: 'message' | 'assign') => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    // Both ops write a real task-bridge row (docs/DISPATCH_BRIDGE.md); we only
    // toast success when the POST actually succeeded — never fake it.
    const payload = kind === 'message' ? `@${agent.name} ${text}` : `${text} — assign to ${agent.name}`;
    try {
      await postJson('/api/tasks', { op: 'create', text: payload, from: 'Cassidy' });
      appendAudit({ actor: 'Cassidy', action: kind === 'message' ? 'Messaged agent' : 'Assigned task', target: `${agent.name}: ${text.slice(0, 50)}` });
      pushToast('success', kind === 'message' ? `Message queued to ${agent.name}` : `Task assigned to ${agent.name}`, text.slice(0, 70));
      setDraft('');
    } catch {
      pushToast('danger', 'Couldn’t queue that', 'The task bridge didn’t accept it — try again.');
    } finally {
      setSending(false);
    }
  };

  // Orchestrator lifecycle is an [I DO] — flag it honestly, never fake a control.
  const flagControl = (verb: string) => {
    appendAudit({ actor: 'Cassidy', action: 'Flagged orchestrator control', target: `${verb} ${agent.name}` });
    pushToast('info', 'Orchestrator control is an [I DO]', `${verb} ${agent.name} — flagged to #needs-admin`);
  };

  const btn44: CSSProperties = { minHeight: 44 };
  const paused = agent.status === 'idle' || agent.status === 'blocked';

  return (
    <Dialog open title={`${agent.name} · ${agent.role}`} onClose={onClose} width={560}
      footer={<Button variant="ghost" style={btn44} onClick={onClose}>Close</Button>}>
      {/* meta row */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{agent.glyph}</span>
        {agent.model && <Tag tone={modelTone(agent.model)} style={{ padding: '2px 9px', fontSize: 10.5 }}>{agent.model}</Tag>}
        <StatusPill status={agent.status} />
        <span style={{ ...mono, flex: '1 1 160px', minWidth: 0, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.task}</span>
      </div>

      {/* thread */}
      {msgs.length === 0 ? (
        <div style={{ border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)', padding: 18, textAlign: 'center', color: 'var(--muted)' }}>
          <div style={{ fontSize: 13, color: 'var(--ink)', marginBottom: 4 }}>No thread yet</div>
          <div style={mono}>{threadConnect ?? 'Connect the orchestrator'} · conversation appears once the crew talks</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '42vh', overflowY: 'auto', paddingRight: 2 }}>
          {threadsPlaceholder && <div style={{ ...mono, textAlign: 'center' }}>sample conversation · connect the orchestrator for live</div>}
          {msgs.map((m, i) => {
            const d = DIR[m.dir];
            return (
              <div key={i} style={{ alignSelf: d.align, maxWidth: '88%', minWidth: 0, background: d.soft, border: `1px solid ${d.line}`, borderRadius: 'var(--radius-md)', padding: '9px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span aria-hidden="true" style={{ color: d.ink, fontSize: 12, fontWeight: 700 }}>{d.arrow}</span>
                  <span style={{ ...subLabel, color: d.ink }}>{d.word}</span>
                  <span style={mono}>{d.sub}</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 12, color: 'var(--ink)' }}>{m.from}</span>
                  <span style={{ ...mono, marginLeft: 'auto' }}>{m.time}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>{m.text}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* compose (message / assign → task bridge) */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <label htmlFor="crew-compose" style={{ ...subLabel, display: 'block', marginBottom: 6 }}>message {agent.name}</label>
        <textarea
          id="crew-compose"
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          placeholder={`Type a message or a task for ${agent.name}…`}
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--ink)', font: 'inherit', fontSize: 13, padding: '9px 11px', minWidth: 0 }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <Button size="sm" style={btn44} disabled={!draft.trim() || sending} onClick={() => void dispatch('message')}>
            {sending ? 'Queuing…' : 'Message'}
          </Button>
          <Button variant="secondary" size="sm" style={btn44} disabled={!draft.trim() || sending} onClick={() => void dispatch('assign')}>
            Assign task
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" style={btn44} onClick={() => flagControl(paused ? 'Resume' : 'Pause')}>
            {paused ? 'Resume' : 'Pause'}
          </Button>
          <Button variant="ghost" size="sm" style={btn44} onClick={() => flagControl('Spin up a copy of')}>
            Spin-up
          </Button>
        </div>
        <div style={{ ...mono, marginTop: 8, lineHeight: 1.5 }}>
          message · assign → task bridge · pause/spin-up are orchestrator [I DO]s (flagged to #needs-admin)
        </div>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- screen */

export function Crew() {
  const crew = useContract('crew', { pollMs: 15_000 });
  const threads = useContract('threads', { pollMs: 20_000 });
  const trace = useContract('trace', { pollMs: 15_000 });
  const kanban = useContract('kanban', { pollMs: 30_000 });
  const roster = useContract('roster', { pollMs: 0 });
  const connect = integrationFor('crew')?.connect;
  const { pushToast } = useOps();

  const [openId, setOpenId] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const openThread = useCallback((id: string) => setOpenId(id), []);
  const closeThread = useCallback(() => setOpenId(null), []);

  const openAgent = openId ? (crew.data ?? []).find((c) => c.id === openId) ?? null : null;
  const threadMsgs = openId ? threads.data?.[openId] ?? [] : [];

  // Thread dialog a11y: label it from its <h3>, focus in, trap Tab, Esc to
  // close, return focus to the opener. (The ops confirm dialog has its own
  // trap; these never overlap.)
  useEffect(() => {
    if (!openId) return;
    opener.current = document.activeElement as HTMLElement | null;
    const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
    if (dialog) {
      const h3 = dialog.querySelector('h3');
      if (h3) {
        if (!h3.id) h3.id = 'crew-thread-title';
        dialog.setAttribute('aria-labelledby', h3.id);
      }
      dialog.setAttribute('aria-label', 'Agent conversation thread');
    }
    const focusables = () =>
      dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')).filter(
            (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
          )
        : [];
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpenId(null);
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      opener.current?.focus();
    };
  }, [openId]);

  const running = (crew.data ?? []).filter((c) => c.status === 'running').length;
  const active = (crew.data ?? []).filter((c) => c.status !== 'idle').length;
  const costTotal = (crew.data ?? []).reduce((sum, c) => sum + c.cost, 0);

  const rosterBadges = roster.data ? deriveBadges({ roster: roster.data }) : [];
  const leadCount = rosterBadges.filter((b) => b.lead).length;
  const memberCount = rosterBadges.filter((b) => !b.lead).length;

  const cardKey = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  return (
    <div data-screen-label="Crew" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === ATLAS COORDINATOR (scaffold, top of surface) === */}
      <AtlasPanel />

      {/* === MACHINE HEARTBEAT (is the always-on box beating?) === */}
      <HeartbeatTile />

      {/* === HIERARCHY (You → Atlas → leads → workers) === */}
      <Widget query={crew} title="// hierarchy — who reports to whom" minHeight={140} rows={2} connect={connect} badge="connect me"
        errorLabel="Orchestrator offline" errorSub="Couldn't reach Atlas." emptyLabel="No agents enrolled">
        {(c) => <Hierarchy data={c} onOpen={openThread} />}
      </Widget>

      {/* === AGENT CARDS (real state · click → thread) === */}
      <Widget query={crew} title="// the crew" minHeight={220} rows={3} connect={connect} badge="connect me"
        aside={<span style={{ ...mono, color: 'var(--accent-ink)' }}>{running} running · {active} active</span>}
        errorLabel="Orchestrator offline" errorSub="Couldn't reach Atlas." emptyLabel="No agents running">
        {(c) => (
          <div className="mc-auto mc-auto-md">
            {deriveCrew({ crew: c }).map((a) => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => openThread(a.id)}
                onKeyDown={cardKey(() => openThread(a.id))}
                aria-label={`Open ${a.name} conversation thread`}
                style={{ background: 'var(--surface)', border: `1px solid ${a.borderCol}`, borderRadius: 'var(--radius-lg)', boxShadow: a.glowShadow, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer', minWidth: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 20, flex: 'none', background: a.badgeBg, border: `1px solid ${a.badgeLine}` }}>{a.glyph}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{a.name}</span>
                      {a.lead && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--primary)' }}>lead</span>}
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.role}</div>
                  </div>
                  <StatusPill status={a.status} />
                </div>
                {/* model + tasks-done chips */}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7 }}>
                  {a.model && <Tag tone={modelTone(a.model)} style={{ padding: '2px 9px', fontSize: 10.5 }}>{a.model}</Tag>}
                  {a.tasksDone != null && <span style={mono}>{a.tasksDone} done today</span>}
                  <span aria-hidden="true" style={{ ...mono, marginLeft: 'auto', color: 'var(--muted)' }}>thread ›</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, minHeight: 39 }}>{a.task}</div>
                {/* tokens · elapsed · $cost footer + cost-share microbar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--border)', paddingTop: 12, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>
                  <span>tokens <span style={{ color: 'var(--ink)' }}>{a.tokens}</span></span>
                  <span aria-hidden="true">·</span>
                  <span>{a.elapsed}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--accent-ink)' }}>${a.cost}</span>
                </div>
                <div aria-hidden="true" style={{ height: 3, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden', marginTop: -7 }}>
                  <div style={{ height: '100%', width: `${a.costPct}%`, borderRadius: 999, background: a.lead ? 'var(--primary)' : 'var(--accent)' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Widget>

      {/* === LIVE TRACE (1.4fr) + PER-AGENT COST (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.4fr 1fr' } as CSSProperties}>
        <Widget query={trace} title="// live trace" minHeight={180} rows={4} connect={connect} badge="connect me"
          aside={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 7px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
              live
            </span>
          }
          errorLabel="Trace dropped" errorSub="The step stream disconnected." emptyLabel="No steps yet">
          {(t) => (
            <div>
              {deriveTrace({ trace: t }).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 13 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', marginTop: 4, background: s.dotColor, boxShadow: `0 0 7px ${s.dotColor}`, border: '2px solid var(--bg)', flex: 'none' }} />
                    {s.notLast && <span style={{ flex: 1, width: '1.5px', background: 'var(--border)', marginTop: 3, minHeight: 14 }} />}
                  </div>
                  <div style={{ paddingBottom: 14, flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--ink)' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>{s.agent}</span>{' '}
                      <span style={{ color: 'var(--muted)' }}>{s.step}</span>
                    </div>
                    <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{s.dur} · {s.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Widget>

        {/* Per-agent cost today */}
        <Widget query={crew} title="// per-agent cost · today" minHeight={180} rows={4} connect={connect} badge="connect me"
          aside={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>${costTotal} today</span>}
          errorLabel="Cost unavailable" emptyLabel="No spend yet">
          {(c) => (
            <div>
              {deriveCrew({ crew: c }).map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '6px 0' }}>
                  <span style={{ fontSize: 15, width: 20, textAlign: 'center', flex: 'none' }}>{a.glyph}</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 500, fontSize: 12.5, color: 'var(--ink)', width: 50, flex: 'none' }}>{a.name}</span>
                  <div style={{ flex: 1, height: 7, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${a.costPct}%`, borderRadius: 999, background: 'var(--primary)', boxShadow: '0 0 10px var(--glow-iris)' }} />
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink)', width: 40, textAlign: 'right', flex: 'none' }}>${a.cost}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      </div>

      {/* === PIPELINE (full-width, 4-col kanban) === */}
      <Widget query={kanban} title="// pipeline" minHeight={160} rows={3} connect={connect} badge="connect me"
        errorLabel="Board unavailable" emptyLabel="Pipeline empty">
        {(k) => (
          <div className="mc-auto mc-auto-sm">
            {deriveKanban({ kanban: k }).map((col) => (
              <div key={col.name} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: col.accent }} />
                    {col.name}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{col.count}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {col.items.map((it, i) => (
                    <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 10, fontSize: 12.5, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, flex: 'none' }}>{it.who}</span>
                      <span style={{ flex: 1 }}>{it.t}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Widget>

      {/* === CREW BADGE SET (compass medallions) === */}
      <Widget query={roster} title="// crew badge set" minHeight={120} rows={2} connect={connect} badge="connect me"
        aside={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{leadCount} leads · {memberCount} crew</span>}
        errorLabel="Roster unavailable" emptyLabel="No agents enrolled">
        {(r) => {
          const badges = deriveBadges({ roster: r });
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px 14px' }}>
              {badges.map((b) => (
                <div key={b.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, width: 78 }}>
                  <div style={{ position: 'relative', width: 58, height: 58 }}>
                    <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: b.badgeBg, border: `1px solid ${b.badgeLine}`, display: 'grid', placeItems: 'center', fontSize: 25 }}>{b.glyph}</div>
                    <span aria-hidden="true" style={{ position: 'absolute', top: -3, left: '50%', transform: 'translateX(-50%)', width: '1.5px', height: 5, borderRadius: 1, background: b.ink }} />
                    <span aria-hidden="true" style={{ position: 'absolute', bottom: -3, left: '50%', transform: 'translateX(-50%)', width: '1.5px', height: 5, borderRadius: 1, background: b.ink }} />
                    <span aria-hidden="true" style={{ position: 'absolute', left: -3, top: '50%', transform: 'translateY(-50%)', width: 5, height: '1.5px', borderRadius: 1, background: b.ink }} />
                    <span aria-hidden="true" style={{ position: 'absolute', right: -3, top: '50%', transform: 'translateY(-50%)', width: 5, height: '1.5px', borderRadius: 1, background: b.ink }} />
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>{b.name}</span>
                </div>
              ))}
              {/* + new agent slot — onboarding is a founder [I DO], flagged not faked. */}
              <button
                type="button"
                aria-label="Request a new agent"
                onClick={() => pushToast('info', 'Agent onboarding is an [I DO]', 'Flagged to #needs-admin')}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, width: 78, background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', font: 'inherit' }}
              >
                <span style={{ width: 58, height: 58, borderRadius: '50%', border: '1.5px dashed var(--border)', display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>new</span>
              </button>
            </div>
          );
        }}
      </Widget>

      {/* === THREAD DIALOG === */}
      {openAgent && (
        <ThreadDialog agent={openAgent} msgs={threadMsgs} threadsPlaceholder={threads.isPlaceholder} onClose={closeThread} />
      )}
    </div>
  );
}
